// ── Recipe editor (v1 clients) ──────────────────────────────────────────────
//
// V1 clients define verification via recipes (entity_profiles.json) rather than
// the v2 workflow engine. The recipe's check types / order / modifiers persist
// to the store (they ARE the entity_profiles config); the surrounding v1 config
// files (routes, kycutility, manual_kyc) are shown as mockup sections.
import { useState } from 'react'
import { Check } from 'lucide-react'
import { useOpsCenterStore } from '../store'
import { Checkbox, Field, SectionHeader, SectionLabel, SelectInput, TextInput, Toggle } from './drawer/fields'
import { ConfigSection } from './drawer/ConfigSection'
import { KYC_PROVIDER_LABELS, KycProposedConfig } from './drawer/KycProposedConfig'
import { AmlProposedConfig } from './drawer/AmlProposedConfig'

const CHECK_TYPES = ['blacklist', 'one_plus', 'two_plus', 'two_plus_both', 'id', 'id_only', 'pep', 'pep_media', 'idvalidate', 'manual']

function RoutesSection() {
  const [country, setCountry] = useState('AU')
  const [ent, setEnt] = useState('equifax, equifax-prop')
  const [id, setId] = useState('equifax-docs')
  const [aml, setAml] = useState('complyadvantage')
  return (
    <ConfigSection title="Cascade routing (check → connector)" file="routes">
      <Field label="Country">
        <SelectInput value={country} onChange={(e) => setCountry(e.target.value)}>
          {['AU', 'NZ', 'default'].map((c) => <option key={c} value={c}>{c}</option>)}
        </SelectInput>
      </Field>
      <Field label="Entity checks (two_plus) → connectors"><TextInput value={ent} onChange={(e) => setEnt(e.target.value)} /></Field>
      <Field label="ID checks → connectors"><TextInput value={id} onChange={(e) => setId(e.target.value)} /></Field>
      <Field label="AML checks (pep) → connectors"><TextInput value={aml} onChange={(e) => setAml(e.target.value)} /></Field>
      <p className="text-[10px] text-[#5b6b85]">Comma order = cascade order (tries first, falls to next on failure).</p>
    </ConfigSection>
  )
}

function KycUtilitySection() {
  const [attestationRequired, setAttestationRequired] = useState(false)
  const [dupScoring, setDupScoring] = useState(true)
  const [amlAging, setAmlAging] = useState('0')
  const [defaultAging, setDefaultAging] = useState('never')
  return (
    <ConfigSection title="Core settings" file="kycutility">
      <Toggle label="Attestation required (biometrics)" checked={attestationRequired} onChange={setAttestationRequired} />
      <Toggle label="Duplicate scoring" checked={dupScoring} onChange={setDupScoring} />
      <Field label="Check aging — AML"><TextInput value={amlAging} onChange={(e) => setAmlAging(e.target.value)} placeholder="0" /></Field>
      <Field label="Check aging — default"><TextInput value={defaultAging} onChange={(e) => setDefaultAging(e.target.value)} placeholder="never" /></Field>
    </ConfigSection>
  )
}

function ManualKycSection() {
  const [maxDocs, setMaxDocs] = useState('2')
  const [passingScore, setPassingScore] = useState('100')
  return (
    <ConfigSection title="Manual KYC scoring" file="manual_kyc">
      <Field label="Max documents"><TextInput type="number" value={maxDocs} onChange={(e) => setMaxDocs(e.target.value)} /></Field>
      <Field label="Passing score"><TextInput type="number" value={passingScore} onChange={(e) => setPassingScore(e.target.value)} /></Field>
    </ConfigSection>
  )
}

export function RecipeEditor({ recipeId }: { recipeId: string }) {
  const recipe = useOpsCenterStore((s) => s.recipes.find((r) => r.id === recipeId))
  const updateRecipe = useOpsCenterStore((s) => s.updateRecipe)

  if (!recipe) {
    return <div className="rounded-lg border border-[#1e2c44] bg-[#0f1729] p-6 text-xs text-[#5b6b85]">Recipe not found.</div>
  }

  const toggleCheck = (c: string) => {
    const next = recipe.checkType.includes(c) ? recipe.checkType.filter((x) => x !== c) : [...recipe.checkType, c]
    updateRecipe(recipe.id, { checkType: next })
  }

  return (
    <div className="rounded-lg border border-[#1e2c44] bg-[#0f1729] p-4 flex flex-col gap-4 max-w-3xl">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-[#e2e8f0]">{recipe.longName}</h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e2c44] text-[#8fa0bd]">v1 recipe</span>
        </div>
        <p className="font-mono text-[10px] text-[#5b6b85] mt-0.5">{recipe.name}</p>
      </div>

      <SectionHeader title="Recipe (check types & order)" hint="entity_profiles" />

      <Field label="Long name">
        <TextInput value={recipe.longName} onChange={(e) => updateRecipe(recipe.id, { longName: e.target.value })} />
      </Field>

      <Field label="Check types">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {CHECK_TYPES.map((c) => (
            <Checkbox key={c} label={c} checked={recipe.checkType.includes(c)} onChange={() => toggleCheck(c)} />
          ))}
        </div>
      </Field>

      <Field label="Check order">
        <TextInput value={recipe.checkOrder} onChange={(e) => updateRecipe(recipe.id, { checkOrder: e.target.value })} placeholder="blacklist, !id, ent, idv, aml" />
      </Field>

      <Toggle label="mKYC modifier (manual when kyc.method=manual)" checked={recipe.mkyc} onChange={(v) => updateRecipe(recipe.id, { mkyc: v })} />

      {/* Live entity_profiles.json preview */}
      <div className="rounded-md border border-[#1e2c44] bg-[#0b1220] p-2.5">
        <div className="flex items-center gap-1.5 text-[10px] text-[#5b6b85] mb-1">
          <Check size={11} className="text-[#4ade80]" /> entity_profiles.json (this recipe)
        </div>
        <pre className="text-[10px] font-mono text-[#8fa0bd] whitespace-pre-wrap break-all">
{JSON.stringify({
  [recipe.name]: {
    long_name: recipe.longName,
    policies: { default: { check_type: recipe.checkType.join(','), check_order: recipe.checkOrder.split(',').map((s) => s.trim()) } },
    ...(recipe.mkyc ? { modifiers: [{ name: 'mkyc', key: 'extra_data:kyc.method', value: 'manual' }] } : {}),
  },
}, null, 2)}
        </pre>
      </div>

      <SectionHeader title="Supporting v1 config" hint="Mockup · not saved" />
      <RoutesSection />
      <KycUtilitySection />
      <ManualKycSection />

      <SectionLabel>KYC data-source connectors</SectionLabel>
      {/* No single provider for a recipe cascade → render all KYC connectors + risks. */}
      <KycProposedConfig providers={KYC_PROVIDER_LABELS} />

      <SectionLabel>AML connector</SectionLabel>
      <AmlProposedConfig />
    </div>
  )
}
